import { Link } from "react-router";
import { LuArrowLeft } from "react-icons/lu";
import ActionBar from "../../../common/ActionBar";
import TabBar from "../../../common/TabBar";
import NetworksTab from "./children/NetworksTab";
import PreEnrollmentTab from "./children/PreEnrollmentTab";
import PlanAffiliationTab from "./children/PlanAffiliationTab";
import PageModals from "./children/PageModals";
import { useConfig } from "./useConfig";
import { ConfigTabId, NETWORKS_LIST_ROUTE } from "../../../common/constants";
import "./Config.scss";

/**
 * Client network configuration screen — presentational shell delegating state to
 * {@link useConfig}.
 *
 * @returns {import('react').JSX.Element}
 */
const NetworksConfig = () => {
    const {
        clientNameParam,
        clientName,
        networkConfigs,
        preEnrollment,
        planAffiliation,
        selectedTabId,
        modals,
        savedSuccess,
        isSaveEnabled,
        networkNameOptions,
        clientOptions,
        seriesOptions,
        configTabs,
        modalHandlers,
        urlValidation,
        isLoading,
        isError,
        loadErrorMessage,
        handleClientNameChange,
        handleNetworkFieldChange,
        handleDirectoryTypeChange,
        handleIncludePreEnrollmentChange,
        handleSeriesChange,
        handlePreferredChange,
        handleExternalFieldChange,
        handleDeleteClick,
        handleAddNetwork,
        handleCustomContentChange,
        handleDisplayPcpActivationBadgeChange,
        handleDisplayReferralRequiredBadgeChange,
        handleSiteUrlChange,
        handleSiteUrlBlur,
        handlePublishClick,
        handlePlanTypeChange,
        handleClientIdChange,
        handleNetworkAffiliationChange,
        handleBackClick,
        handleTabChange,
        handleSave,
        openModal,
    } = useConfig();

    const showTabContent = !isLoading && !isError;

    return (
        <div className="networks-config-page">
            <div className="networks-config-page__top">
                <Link
                    to={NETWORKS_LIST_ROUTE}
                    className="networks-config-page__back"
                    aria-label="Back to networks"
                    onClick={handleBackClick}
                >
                    <LuArrowLeft aria-hidden size={18} />
                    <span>Networks</span>
                </Link>
                <h1 className="networks-config-page__title">
                    {clientNameParam
                        ? `Configure ${clientNameParam}`
                        : "Create new client"}
                </h1>
                <ActionBar
                    layout="end"
                    alerts={
                        savedSuccess
                            ? [
                                  {
                                      tone: "success",
                                      message:
                                          "You have successfully submitted your work.",
                                  },
                              ]
                            : []
                    }
                    actions={[
                        {
                            label: "Save",
                            variant: "accent",
                            disabled: !isSaveEnabled || isLoading || isError,
                            onClick: handleSave,
                        },
                    ]}
                />
            </div>

            {isLoading && (
                <p className="networks-config-page__loading" role="status">
                    Loading client configuration...
                </p>
            )}

            {isError && (
                <p className="networks-config-page__error" role="alert">
                    {loadErrorMessage}
                </p>
            )}

            <TabBar
                ariaLabel="Client configuration sections"
                selectedTabId={selectedTabId}
                tabs={configTabs}
                trailingLabel={clientNameParam || undefined}
                className="plan-manager-tab-bar--with-divider"
                onTabChange={handleTabChange}
            />

            {showTabContent && selectedTabId === ConfigTabId.NETWORKS ? (
                <NetworksTab
                    clientName={clientName}
                    networkConfigs={networkConfigs}
                    seriesOptions={seriesOptions}
                    onClientNameChange={handleClientNameChange}
                    onFieldChange={handleNetworkFieldChange}
                    onDirectoryTypeChange={handleDirectoryTypeChange}
                    onIncludePreEnrollmentChange={
                        handleIncludePreEnrollmentChange
                    }
                    onSeriesChange={handleSeriesChange}
                    onPreferredChange={handlePreferredChange}
                    onExternalFieldChange={handleExternalFieldChange}
                    onDeleteClick={handleDeleteClick}
                    onAddClick={handleAddNetwork}
                />
            ) : null}

            {showTabContent && selectedTabId === ConfigTabId.PRE_ENROLLMENT ? (
                <PreEnrollmentTab
                    preEnrollment={preEnrollment}
                    urlValidation={urlValidation}
                    onCustomContentChange={handleCustomContentChange}
                    onDisplayPcpActivationBadgeChange={
                        handleDisplayPcpActivationBadgeChange
                    }
                    onDisplayReferralRequiredBadgeChange={
                        handleDisplayReferralRequiredBadgeChange
                    }
                    onSiteUrlChange={handleSiteUrlChange}
                    onSiteUrlBlur={handleSiteUrlBlur}
                    onEditUrlClick={() => openModal("confirmEditUrl")}
                    onPublishClick={handlePublishClick}
                />
            ) : null}

            {showTabContent &&
            selectedTabId === ConfigTabId.PLAN_AFFILIATION ? (
                <PlanAffiliationTab
                    planAffiliation={planAffiliation}
                    clientOptions={clientOptions}
                    networkNameOptions={networkNameOptions}
                    onPlanTypeChange={handlePlanTypeChange}
                    onClientIdChange={handleClientIdChange}
                    onNetworkAffiliationChange={handleNetworkAffiliationChange}
                />
            ) : null}

            <PageModals flags={modals} handlers={modalHandlers} />
        </div>
    );
};

export default NetworksConfig;
